import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import Sandbox, { SandboxConfig } from "@mapmycustomers/shared/types/Sandbox";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button, LoadingSpinner } from "@mapmycustomers/ui";

import { RootState } from "@app/store/rootReducer";

import settingsAnalytics from "../../settingsAnaytics";
import SettingPage from "../common/SettingPage";

import EditPane from "./component/EditPane";
import SandboxNameModal from "./component/SandboxNameModal";
import SandboxState from "./component/SandboxState";
import styles from "./Sandbox.module.scss";
import { getCurrentSandbox, isSandboxLoading } from "./store";
import {
  createSandboxConfig,
  deleteSandboxConfig,
  initSandboxPage,
  updateSandboxConfig,
} from "./store/actions";

interface Props {
  currentSandbox?: Sandbox;
  initSandboxPage: typeof initSandboxPage.request;
  loading: boolean;
  onCreateSandboxConfig: typeof createSandboxConfig.request;
  onDeleteSandboxConfig: typeof deleteSandboxConfig.request;
  onUpdateSandboxConfig: typeof updateSandboxConfig.request;
}

const SandboxPage: React.FC<Props> = ({
  currentSandbox,
  initSandboxPage,
  loading,
  onCreateSandboxConfig,
  onDeleteSandboxConfig,
  onUpdateSandboxConfig,
}) => {
  const intl = useIntl();
  const [editPaneVisible, showEditPane, hideEditPane] = useBoolean();
  const [nameModalVisible, showNameModal, hideNameModal] = useBoolean();

  useEffect(() => {
    initSandboxPage();
  }, [initSandboxPage]);
  const handleCloseNameModal = useCallback(
    (name?: string) => {
      hideNameModal();
      if (name) {
        onCreateSandboxConfig({ callback: showEditPane, name });
      }
    },
    [onCreateSandboxConfig, hideNameModal, showEditPane]
  );

  const handleHideCreateConfigPane = useCallback(
    (config?: SandboxConfig) => {
      hideEditPane();
      if (config) {
        onUpdateSandboxConfig(config);
      }
    },
    [hideEditPane, onUpdateSandboxConfig]
  );

  const handleReSaveConfig = useCallback(() => {
    if (currentSandbox?.config) {
      onUpdateSandboxConfig(currentSandbox?.config);
    }
  }, [currentSandbox?.config, onUpdateSandboxConfig]);

  return (
    <SettingPage
      pageContentClassName={styles.container}
      title={intl.formatMessage({
        id: "settings.sandboxEnvironment.title",
        defaultMessage: "Sandbox Environment",
        description: "Title of settings Sandbox Environment page",
      })}
    >
      <p>
        {intl.formatMessage({
          id: "settings.sandboxEnvironment.description",
          defaultMessage:
            "Set up a sandbox environment to test functionality without affecting your account data. For example, use a Sandbox to test an Integration configuration.",
          description: "Description of settings Sandbox Environment page",
        })}
      </p>
      {loading ? (
        <LoadingSpinner />
      ) : currentSandbox ? (
        <SandboxState
          onDelete={onDeleteSandboxConfig}
          onEdit={showEditPane}
          onReSave={handleReSaveConfig}
          sandbox={currentSandbox}
        />
      ) : (
        <div>
          <Button
            onClick={() => {
              settingsAnalytics?.clicked(["Sandbox", "Create Sandbox"]);
              showNameModal();
            }}
            type="primary"
          >
            {intl.formatMessage({
              id: "settings.sandboxEnvironment.createSandbox",
              defaultMessage: "Create Sandbox",
              description: "Settings Sandbox Environment - create Sandbox",
            })}
          </Button>
        </div>
      )}
      {editPaneVisible && (
        <EditPane config={currentSandbox?.config} onHide={handleHideCreateConfigPane} />
      )}
      {nameModalVisible && <SandboxNameModal onHide={handleCloseNameModal} />}
    </SettingPage>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentSandbox: getCurrentSandbox(state),
  loading: isSandboxLoading(state),
});

const mapDispatchToProps = {
  initSandboxPage: initSandboxPage.request,
  onCreateSandboxConfig: createSandboxConfig.request,
  onDeleteSandboxConfig: deleteSandboxConfig.request,
  onUpdateSandboxConfig: updateSandboxConfig.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(SandboxPage);
